import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Layers, ShieldCheck, Info } from "lucide-react";

/**
 * Superuser-only dialog to toggle per-client module visibility.
 * Renders nothing for non-superusers (caller should also gate the trigger).
 */
const ModuleAccessDialog = ({ open, onOpenChange, targetUser }) => {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [flags, setFlags] = useState({});
  const [effective, setEffective] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isSuperuser = user?.role === "superuser";
  const isAdminTarget = targetUser && ["admin", "superuser"].includes(targetUser.role);

  useEffect(() => {
    if (!open || !targetUser) return;
    (async () => {
      setLoading(true);
      try {
        const [catRes, accRes] = await Promise.all([
          api.get("/module-access/catalog"),
          api.get(`/module-access/${targetUser.id}`),
        ]);
        setCatalog(catRes.data.modules);
        // stored holds ONLY user-level overrides; effective merges role + defaults
        setFlags({ ...catRes.data.defaults, ...(accRes.data.stored || {}) });
        setEffective(accRes.data.effective || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load module access");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, targetUser]);

  const toggle = (key) => setFlags((f) => ({ ...f, [key]: !f[key] }));

  const save = async () => {
    if (!isSuperuser) return;
    setSaving(true);
    try {
      const r = await api.put(`/module-access/${targetUser.id}`, flags);
      setEffective(r.data.effective);
      toast.success(`Module access updated for ${targetUser.full_name || targetUser.email}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save module access");
    } finally {
      setSaving(false);
    }
  };

  if (!targetUser) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="module-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Layers size={18} className="text-blue-600" />
            Module Access
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Toggle which investment modules <span className="font-semibold text-slate-700">{targetUser.full_name || targetUser.email}</span> can see.
          </DialogDescription>
        </DialogHeader>

        {/* Admin-target notice */}
        {isAdminTarget && (
          <div className="p-3 rounded border border-blue-200 bg-blue-50 text-sm text-blue-900 flex gap-2">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            <span>Admin and superuser accounts automatically see every module. Per-user flags are ignored for them.</span>
          </div>
        )}

        {/* Non-superuser notice */}
        {!isSuperuser && !isAdminTarget && (
          <div className="p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900 flex gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>Only superusers can change module access. This view is read-only.</span>
          </div>
        )}

        {loading ? (
          <div className="h-40 bg-slate-100 animate-pulse rounded" />
        ) : (
          <div className="space-y-2" data-testid="module-access-list">
            {catalog.map((mod) => {
              const on = isAdminTarget ? true : !!flags[mod.key];
              return (
                <div key={mod.key}
                     className="flex items-center justify-between gap-4 p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                     data-testid={`module-row-${mod.key}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{mod.label}</p>
                    <p className="text-xs text-slate-500">{mod.description}</p>
                    <code className="text-[10px] text-slate-400 font-mono">{mod.route}</code>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {on ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Visible</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 border-slate-200 border">Hidden</Badge>
                    )}
                    <Switch
                      checked={on}
                      disabled={!isSuperuser || isAdminTarget || saving}
                      onCheckedChange={() => toggle(mod.key)}
                      data-testid={`module-toggle-${mod.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          {isSuperuser && !isAdminTarget && (
            <Button
              onClick={save}
              disabled={saving || loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="module-access-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleAccessDialog;
